import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { AnalyticsStatus, useAnalytics } from '@treams/treams-analytics';
import { HistoryIcon, SyncIcon } from '@treams/treams-icons';
import { Button, Paragraph, Tooltip, tokens } from '@treams/treams-ui';

const PRODUCTION_GRAPHQL_ENDPOINT = 'https://api.ontreams.com/graphql';

/** Rebuilds the analytics snapshot of this workspace and reloads it. Non-production only. */
export function AnalyticsRefreshButton() {
	const { t } = useTranslation();
	const { lastSyncedAt, loadMetrics, status } = useAnalytics();

	const endpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT ?? PRODUCTION_GRAPHQL_ENDPOINT;

	if (endpoint === PRODUCTION_GRAPHQL_ENDPOINT) {
		return null;
	}

	const isRefreshing = status === AnalyticsStatus.Loading || status === AnalyticsStatus.Ingesting;
	const canRefresh =
		status === AnalyticsStatus.Failed || status === AnalyticsStatus.NotExists || !!lastSyncedAt;

	return (
		<Button
			title={t('analytics.overview.syncMetrics')}
			type="secondary"
			icon={<SyncIcon />}
			onClick={() => loadMetrics({ generate: true })}
			loading={isRefreshing}
			disabled={isRefreshing || !canRefresh}
		/>
	);
}

export function AnalyticsSyncStatus() {
	const { t } = useTranslation();
	const { lastSyncedAt } = useAnalytics();

	return (
		<Tooltip content={t('analytics.overview.lastUpdatedTooltip')} portal position="top">
			<Flex alignItems="center" style={{ gap: tokens.gap.inline }}>
				<HistoryIcon size={16} color={tokens.icon.secondary} />
				<Paragraph small>
					{lastSyncedAt
						? t('analytics.overview.lastUpdatedLabel', {
								time: moment(lastSyncedAt).format('LL'),
							})
						: t('analytics.snapshots.checking')}
				</Paragraph>
			</Flex>
		</Tooltip>
	);
}
